import { ArrowUpRight, X } from '@griply/assets';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Checkbox } from 'shared/components/ui/checkbox';
import { Icon } from 'shared/components/ui/icon';
import { IconButton } from 'shared/components/ui/icon-button';
import { DesktopPlatform } from 'shared/utils/get-desktop-download';

import * as Styled from './open-in-desktop-message.style';

export type OpenInDesktopMessageProps = {
  desktopUrl: string;
  downloadUrl: string;
  platform: DesktopPlatform;
  isLaunched: boolean;
  alwaysOpen: boolean;
  onOpen: () => void;
  onAlwaysOpenChange: (value: boolean) => void;
  onDismiss: () => void;
};

export const OpenInDesktopMessage: React.FC<OpenInDesktopMessageProps> = ({
  desktopUrl,
  downloadUrl,
  platform,
  isLaunched,
  alwaysOpen,
  onOpen,
  onAlwaysOpenChange,
  onDismiss,
}) => {
  const { t } = useTranslation();

  const copyKey = isLaunched
    ? 'messages.open-in-desktop.launched'
    : 'messages.open-in-desktop.default';
  const downloadLabel =
    platform === DesktopPlatform.Mac
      ? t('messages.open-in-desktop.buttons.download.mac')
      : t('messages.open-in-desktop.buttons.download.windows');

  const handleAlwaysOpenChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onAlwaysOpenChange(event.target.checked);

  return (
    <Styled.Container>
      <Styled.CloseButtonContainer>
        <IconButton icon={X} onClick={onDismiss} />
      </Styled.CloseButtonContainer>
      <Styled.IconContainer>
        <Icon icon={ArrowUpRight} />
      </Styled.IconContainer>
      <Styled.Body>
        <Styled.Content>
          <Typography variant="h6" component="span">
            {t(`${copyKey}.title`)}
          </Typography>
          <Styled.Description>{t(`${copyKey}.description`)}</Styled.Description>
        </Styled.Content>

        <Styled.AlwaysOpen>
          <Checkbox
            isSmall
            checked={alwaysOpen}
            onChange={handleAlwaysOpenChange}
          >
            {t('messages.open-in-desktop.always-open.label')}
          </Checkbox>
        </Styled.AlwaysOpen>

        <Styled.Interactions>
          <Button
            variant={Variants.Text}
            color={Colors.TextPrimary}
            size={Sizes.Small}
            as="a"
            // @ts-expect-error -- Button polymorphic `href` prop typing
            href={downloadUrl}
          >
            {downloadLabel}
          </Button>
          <Button
            variant={Variants.Contained}
            color={Colors.Primary}
            size={Sizes.Small}
            as="a"
            // @ts-expect-error -- Button polymorphic `href` prop typing
            href={desktopUrl}
            onClick={onOpen}
          >
            {t(`${copyKey}.open.label`)}
          </Button>
        </Styled.Interactions>
      </Styled.Body>
    </Styled.Container>
  );
};
